<script setup lang="ts">
import { useEscapeEvent } from '@/utilities/UseEscapeEvent';
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';

interface Props {
	modalName: string;
	isOpen: boolean;
}

const props = defineProps<Props>();
const dialog = ref<HTMLDialogElement>();
const store = useStore();

const isDialogOpen = computed(() => props.isOpen);

const close = () => {
	store.commit('updateCurrentModalName', null);
};
const show = () => {
	dialog.value?.showModal();
};

// fire dialog close after transition has finished
function onAfterLeave() {
	dialog.value?.close();
}

watch(
	isDialogOpen,
	(open) => {
		if (open) {
			show();
		} else {
			close();
		}
	},
	{ immediate: true }
);

useEscapeEvent(close);
</script>

<template>
	<dialog
		ref="dialog"
		class="dialog"
		@click.self="close"
	>
		<Transition name="slide" @after-leave="onAfterLeave">
			<div v-if="isDialogOpen">
				<focus-trap	v-model:active="isDialogOpen">
					<div class="dialog__content" v-scroll-lock="true">
						<slot></slot>
					</div>
				</focus-trap>
			</div>
		</Transition>
	</dialog>
</template>

<style scoped lang="scss">
.dialog {
	background-color: #00000070;
	backdrop-filter: blur(2px);
	margin: 0;
	padding: 0;
	border: none;
	min-width: 100vw;
	min-height: 100vh;
	inset: 0;
	overflow: hidden;
}

.dialog::backdrop {
	display: none;
}

.dialog__content {
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
	background-color: $white;
	width: 100vw;
	height: calc(90vh - var(--admin-bar));
	position: fixed;
	top: calc(100vh - calc(90vh - var(--admin-bar)));
	right: 0;
	transform: translateY(0);
	will-change: transform;
	overflow: scroll;
}

.slide-enter-active,
.slide-leave-active {
	transition: transform 300ms ease;
}

.slide-enter-from,
.slide-leave-to{
	transform: translateY(100vh);
}

@media screen and (min-width: 400px) {
	.dialog__content {
		position: fixed;
		border-radius: 0;
		width: 95vw;
		max-width: 565px;
		height: calc(100vh - var(--admin-bar));
		top: var(--admin-bar);
		right: 0;
	}
	.slide-enter-from,
	.slide-leave-to{
		transform: initial;
		transform: translateX(100%);
	}
}
</style>
